import React, { useCallback } from 'react';
import { F2110, F2111, p2201Api } from '../../../services/p2201Api';
import { fmt } from '../helpers';

interface Tab2Props {
  formData: Partial<F2110>;
  details: Partial<F2111>[];
  selectedDetailRow: number;
  isEditing: boolean;
  getField: (key: keyof F2110) => F2110[keyof F2110];
  setDetails: React.Dispatch<React.SetStateAction<Partial<F2111>[]>>;
  setSelectedDetailRow: (idx: number) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
  setDetailField: (rowIdx: number, key: keyof F2111, value: F2111[keyof F2111]) => void;
}

/** Tab 2: Detail Grid (Page2 from ui_spec -- Grid1 + CMDDET buttons) */
const Tab2Detail: React.FC<Tab2Props> = React.memo(({
  formData, details, selectedDetailRow, isEditing,
  getField, setDetails, setSelectedDetailRow,
  addDetailRow, deleteDetailRow, setDetailField,
}) => {

  /** Handle product code blur: auto-fill name, unit, price (BR-06) */
  const handleProductBlur = useCallback(async (rowIdx: number, code: string) => {
    if (!code.trim()) return;
    const info = await p2201Api.getProductInfo(code);
    if (info) {
      setDetails(prev => {
        const next = [...prev];
        const row = { ...next[rowIdx] };
        row.f2111_11a = info.name;
        row.f2111_12 = info.unit;
        row.f2111_14 = info.price;
        // recalc amount
        const qty = row.f2111_13 || 0;
        row.f2111_15 = Math.round(qty * info.price * 100) / 100;
        next[rowIdx] = row;
        return next;
      });
    }
  }, [setDetails]);

  return (
    <div className="tab-content-section">
      {/* Detail buttons: CMDDET1 / CMDDET2 / CMDDETD */}
      <div className="grid-header">
        <h3 className="section-title" style={{ margin: 0, border: 'none', padding: 0 }}>入庫明細</h3>
        <div className="grid-actions">
          <button className="btn btn-sm" onClick={addDetailRow} disabled={!isEditing}>明細新增</button>
          <button className="btn btn-sm btn-danger" onClick={deleteDetailRow}
            disabled={!isEditing || details.length === 0}>明細刪除</button>
        </div>
      </div>

      {/* Detail Grid (Grid1) */}
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }} data-contract-field="f2111_02">項次</th>
              <th style={{ width: 100 }} data-contract-field="f2111_11">產品代號</th>
              <th style={{ width: 200 }} data-contract-field="f2111_11a">產品名稱</th>
              <th style={{ width: 50 }} data-contract-field="f2111_12">單位</th>
              <th style={{ width: 100, textAlign: 'right' }} data-contract-field="f2111_13">數量</th>
              <th style={{ width: 100, textAlign: 'right' }} data-contract-field="f2111_14">單價</th>
              <th style={{ width: 110, textAlign: 'right' }} data-contract-field="f2111_15">金額</th>
              <th style={{ width: 140 }} data-contract-field="f2111_18">摘要</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999', padding: 20 }}>無明細資料</td></tr>
            )}
            {details.map((d, idx) => (
              <tr
                key={idx}
                className={idx === selectedDetailRow ? 'row-selected' : ''}
                onClick={() => setSelectedDetailRow(idx)}
              >
                {/* Column1: seq (readonly) */}
                <td style={{ textAlign: 'center' }}>{d.f2111_02}</td>

                {/* Column2: product code (BR-06 lookup on blur) */}
                <td>
                  <input
                    type="text"
                    value={d.f2111_11 || ''}
                    onChange={e => setDetailField(idx, 'f2111_11', e.target.value)}
                    onBlur={e => handleProductBlur(idx, e.target.value)}
                    disabled={!isEditing}
                    maxLength={12} data-contract-field="f2111_11"
                  />
                </td>

                {/* Column3: product name (readonly) */}
                <td>
                  <input type="text" value={d.f2111_11a || ''} disabled data-contract-field="f2111_11a" />
                </td>

                {/* Column4: unit */}
                <td>
                  <input
                    type="text"
                    value={d.f2111_12 || ''}
                    onChange={e => setDetailField(idx, 'f2111_12', e.target.value)}
                    disabled={!isEditing}
                    maxLength={4} data-contract-field="f2111_12"
                  />
                </td>

                {/* Column5: quantity (BR-07 auto-calc on change) [T-F03] */}
                <td>
                  <input
                    type="number"
                    value={d.f2111_13 ?? ''}
                    onChange={e => setDetailField(idx, 'f2111_13', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ textAlign: 'right' }}
                    step="0.01" data-contract-field="f2111_13"
                  />
                </td>

                {/* Column6: unit price (BR-08 auto-calc on change) [T-F03] */}
                <td>
                  <input
                    type="number"
                    value={d.f2111_14 ?? ''}
                    onChange={e => setDetailField(idx, 'f2111_14', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ textAlign: 'right' }}
                    step="0.01" data-contract-field="f2111_14"
                  />
                </td>

                {/* Column7: amount (readonly, auto-calc) */}
                <td style={{ textAlign: 'right' }}>
                  {fmt(d.f2111_15)}
                </td>

                {/* Remark (txtF2111_18) */}
                <td>
                  <input
                    type="text"
                    value={d.f2111_18 || ''}
                    onChange={e => setDetailField(idx, 'f2111_18', e.target.value)}
                    disabled={!isEditing}
                    maxLength={60} data-contract-field="f2111_18"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Below-grid summary: total items + detail sum (from Page2 spec) */}
      <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>
        <span>總項數: <strong>{getField('f2110_16') as number || details.length}</strong></span>
        <span>明細金額: <strong>{fmt(getField('f2110_17') as number)}</strong></span>
      </div>
    </div>
  );
});

Tab2Detail.displayName = 'Tab2Detail';
export default Tab2Detail;
